import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { cloneDeep } from 'lodash-es';
import { of } from 'rxjs';
import { concatMap, filter, map, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { UserOperation } from '../../../../core/enums/user-operation';
import { FileModel } from '../../../../core/models/file/file.model';
import { ProjectExpenseModel } from '../../../../core/models/finance/expense.model';
import { ShiveDialogService } from '../../../../core/services/controls/shive-dialog.service';
import * as fromFilesActions from '../../../../core/state/files/files.actions';
import { ProjectExpensesHttpService } from '../../../../core/state/projects/expenses/project-expenses-http.service';
import * as fromProjectExpensesActions from '../../../../core/state/projects/expenses/project-expenses.actions';
import { ProjectDataService } from '../state-and-data-handling/project-data.service';
import { ProjectExpensesDialogComponent } from './dialog/project-expenses-dialog/project-expenses-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ProjectExpensesGridService implements OnDestroy {
  // Grid data
  public gridData = signal<ProjectExpenseModel[]>([]);

  // DI
  private readonly projectExpensesHttpService = inject(ProjectExpensesHttpService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly actions$ = inject(Actions);

  // Misc
  private readonly subs = new SubSink();

  constructor() {
    this.httpUpdateCallback();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public fetchProjectExpenses(): void {
    this.projectExpensesHttpService
      .getExpenses()
      .pipe(take(1))
      .subscribe((projectExpenses) => this.gridData.set([...projectExpenses]));
  }

  public openExpenseDialog(
    userOperation: UserOperation,
    expenseGridRow?: ProjectExpenseModel,
  ): void {
    let projectExpense = cloneDeep(expenseGridRow);
    let title = 'Projektausgabe bearbeiten';

    if (userOperation === UserOperation.Create) {
      projectExpense = new ProjectExpenseModel();
      projectExpense.projectId = this.projectDataService.getProjectClone().id;
      title = 'Projektausgabe hinzufügen';
    }

    const dialogRef = this.shiveDialogService.open({
      title,
      content: ProjectExpensesDialogComponent,
      width: 572,
    });
    const dialogCmp = dialogRef.content.instance as ProjectExpensesDialogComponent;
    dialogCmp.projectExpense.set(projectExpense);

    this.subs.sink = dialogRef.result
      .pipe(filter((dialogResult) => dialogResult !== UserOperation.Cancel))
      .subscribe((dialogResult) => {
        const updatedProjectExpense = dialogResult as ProjectExpenseModel;
        if (userOperation === UserOperation.Create) {
          this.projectExpensesHttpService.addProjectExpense(updatedProjectExpense);
          return;
        }

        if (userOperation === UserOperation.Update) {
          this.projectExpensesHttpService.updateProjectExpense(
            updatedProjectExpense,
            projectExpense.attachment as FileModel,
          );
        }
      });
  }

  public deleteExpenseRow(projectExpenseRow: ProjectExpenseModel): void {
    this.projectExpensesHttpService.deleteProjectExpense(projectExpenseRow);
  }

  private httpUpdateCallback(): void {
    this.subs.sink = this.actions$
      .pipe(
        ofType(
          fromProjectExpensesActions.projectExpenseAdded,
          fromProjectExpensesActions.projectExpenseUpdated,
          fromProjectExpensesActions.projectExpenseDeleted,
        ),
        concatMap((action: any) => {
          if (action.type === fromProjectExpensesActions.projectExpenseDeleted.type) {
            return of(true);
          }

          const hasFileWork = !!action.newAttachment || !!action.oldAttachment;
          if (!hasFileWork) {
            return of(true);
          }

          // Deleted and no update afterwards
          if (!!action.oldAttachment && !action.newAttachment) {
            return this.actions$.pipe(
              ofType(fromFilesActions.fileDeleted),
              take(1),
              map(() => true),
            );
          }

          // Added -> wait until upload finished
          if (action.type === fromProjectExpensesActions.projectExpenseAdded.type) {
            return this.actions$.pipe(
              ofType(fromFilesActions.fileUploaded),
              take(1),
              map(() => true),
            );
          }

          // Updated -> wait until update flow finished (delete+upload -> fileUpdated)
          return this.actions$.pipe(
            ofType(fromFilesActions.fileUpdated),
            take(1),
            map(() => true),
          );
        }),
      )
      .subscribe(() => this.fetchProjectExpenses());
  }
}
